import { PouchDoc } from "./pouch";
import { UserConsentRecords } from '../user-consent-interface';
import type { AuthRole } from '../couch-auth';
import { kCoachBillingSecrecy, type CouchUserCoachBilling } from './coach-billing';
import { applySecrecyRules, kFullyCensoredValue, type SecrecyRules } from './secrecy-rules';
import { DateTime } from "luxon";

export enum UserLicenseType {
  Key = 'key',
  PayPro = 'payPro',
  // Paddle is a second web payment provider running alongside PayPro
  // (the $20/mo web subscription). Added as a fourth license-granting
  // provider in `reconcileLicenseState`. See design-docs/paddle-integration.md.
  Paddle = 'paddle',
  GooglePlay = 'googlePlay',
  Apple = 'apple',
  Coach = 'coach',
  // A coach-tier store subscription (Apple / Google Play fixed-tier
  // rail). It grants the coach personal Premium, so it is a
  // license-granting candidate in `reconcileLicenseState` alongside the
  // store providers. See design-docs/coach-slot-pricing.md.
  CoachTier = 'coachTier',
  // A Paddle per-seat coaching subscription (the web coach rail). Like
  // CoachTier it grants the coach personal Premium — the coach's own
  // Premium is seat #1 of the coaching subscription — so it is a
  // license-granting candidate in `reconcileLicenseState`. It is the web
  // sibling of CoachTier. See design-docs/paddle-integration.md
  // ("Phase 4 — Coach per-seat coaching subscription").
  CoachSeat = 'coachSeat',
};

// How many hours past the real billing period end we still honor the license.
// Renewal notifications can lag the actual charge by minutes to hours (PayPro's
// IPNs worst of all), and without this buffer a user briefly loses access during
// an ordinary renewal.
//
// It is NOT PayPro-only, despite what this comment said until #4278. The writers
// that add it: PayPro, Google Play, Paddle (personal AND coach seat), and the
// Google Play half of the coach-tier rail. Apple does not — see below. The
// authoritative per-rail list is the `readStoredExpiryGraceHours` column on
// `LICENSE_RAILS` in ./license-reconciler.ts, which a new provider cannot skip
// without failing to compile.
//
// Anything DISPLAYING one of these dates must go through
// ./subscription-end-instants.ts rather than doing the arithmetic itself: the
// stored value answers "when does my access end", and the provider's own date is
// a day earlier on every graced rail.
//
// Apple does NOT use this constant — Apple's own subscription `status`
// exposes `grace_period` and `billing_retry` states directly, plus
// App Store Server Notifications (`DID_FAIL_TO_RENEW`, `EXPIRED`) deliver
// state transitions promptly, so adding an extra 24h on top would
// over-entitle paid users for a full day past Apple's actual expiry. Apple
// flows in `appleValidate`, `appleServerNotification`, and
// `reconcileAppleSubscriptionAsync` store the raw Apple expiry directly.
// See "License grace period — provider asymmetry" in
// design-docs/iap-subscriptions.md.
export const kPPGLicenseGracePeriodHours = 24;

/** Sentinel for indefinite expiry on both top-level `licenseExpires_utc`
 * and per-provider `expiresDate_utc`. Used by VIP keys, indefinite Coach
 * grants, and admin-granted indefinite licenses. The reconciler treats
 * this as `+Infinity` for expiry comparisons. */
export const NEVER_EXPIRES = 'never';

/**
 * Explicit shape-version marker for the `_users` doc (#4283). A doc written
 * before this field existed has it `undefined`, which means version `1` —
 * today's shape, with no marker at all. This is the one place the version
 * number lives; do not read it as a raw field elsewhere.
 */
export function getUsersDocFormatVersion(doc: Pick<CouchUserInfoBase, 'usersDocFormatVersion'>): number {
  return doc.usersDocFormatVersion ?? 1;
}

/**
 * The `_users` doc shape-version this server release WRITES. Stamped onto
 * every doc this server writes — `couchPutUserInfo` (the one general
 * `_users` write path, `server/functions/src/couchdb-helpers.ts`) stamps it
 * on every call, and the few sites that create a doc via a raw POST instead
 * of going through that function (`couchLogin.ts`, `payProIPN.ts`,
 * `paddle-webhook-handling.ts`) stamp it explicitly in the create body.
 *
 * Kept apart from `MAX_SUPPORTED_USERS_DOC_FORMAT_VERSION` on purpose — the
 * two only match right after a version is first introduced. A later format
 * change must ship in two releases: an "expand" release that raises
 * `MAX_SUPPORTED_USERS_DOC_FORMAT_VERSION` (so every live server can READ
 * the new shape) while `CURRENT_USERS_DOC_FORMAT_VERSION` stays put (so
 * nothing WRITES it yet); only once that release is live does a later
 * "contract" release raise `CURRENT_USERS_DOC_FORMAT_VERSION` to start
 * writing it. `scripts/ci/check-users-doc-back-compat.mjs` enforces that
 * ordering by failing a release that would raise `CURRENT` past what the
 * currently-live server's `MAX_SUPPORTED` already covers. This is exactly
 * the discipline that was missing in #4200: the v2 server that introduced a
 * new credential derivation started writing it in the same release that
 * introduced it, with no earlier release giving v1 (still live) a chance to
 * learn to read it first.
 */
export const CURRENT_USERS_DOC_FORMAT_VERSION = 2;

/**
 * The highest `_users` doc shape-version this server release can READ.
 * `couchGetUserInfo` (couchdb-helpers.ts) refuses to hand back a doc whose
 * version is higher than this — see `UsersDocTooNewError` — instead of
 * silently reading a shape it does not understand. See
 * `CURRENT_USERS_DOC_FORMAT_VERSION` above for why this is a separate
 * constant.
 */
export const MAX_SUPPORTED_USERS_DOC_FORMAT_VERSION = 2;

/**
 * The version marker an UPDATE should put on `doc`: monotonic, so a write can
 * raise the recorded version but never lower it. Lowering it would leave the
 * doc claiming an older shape than its body actually has, which is #4200's
 * failure reached through the marker meant to prevent it.
 *
 * Every read-modify-write path must use this instead of assigning
 * `CURRENT_USERS_DOC_FORMAT_VERSION` itself. A brand-new doc created from
 * scratch has no prior version to preserve, so those few sites may assign
 * `CURRENT` directly. See `design-docs/users-doc-versioning.md` ("Which marker
 * to write depends on what the write does to the body") for the expand-release
 * scenario this exists for, and for the two write shapes that do NOT use this.
 */
export function nextUsersDocFormatVersion(doc: Pick<CouchUserInfoBase, 'usersDocFormatVersion'>): number {
  return Math.max(getUsersDocFormatVersion(doc), CURRENT_USERS_DOC_FORMAT_VERSION);
}

/**
 * True when `doc` was written by a server newer than this one, so its fields
 * must not be interpreted. The read-side counterpart to
 * `nextUsersDocFormatVersion` — one symbol so no call site can drift onto
 * `>=` or forget the absent-means-1 rule. Callers decide what to do with a
 * true result: refuse the request when the doc IS the subject, or skip and
 * count the row when refusing would break a sweep for everyone else.
 */
export function isUsersDocTooNewToRead(doc: Pick<CouchUserInfoBase, 'usersDocFormatVersion'>): boolean {
  return getUsersDocFormatVersion(doc) > MAX_SUPPORTED_USERS_DOC_FORMAT_VERSION;
}

/** The fixed text CouchDB puts in front of a user's name to make the `_id` of
 * that user's document in the `_users` database. Do not write this literal
 * anywhere else — use `toCouchUserDocId` and `toBareUid` below. */
export const kCouchUserDocIdPrefix = 'org.couchdb.user:';

/**
 * A bare Firebase uid (`abc123`), as opposed to the `_users` document id CouchDB
 * stores that user under (`org.couchdb.user:abc123`).
 *
 * Both are text, so without this type the compiler cannot tell them apart, and a
 * function can hand back the wrong one without anybody noticing. What that costs
 * when it happens is spelled out on `toCouchUserDocId` below; the incident it
 * refers to is written up in `design-docs/paddle-integration.md`.
 *
 * The `__brand` field is a phantom type — it exists only for the compiler and
 * there is no such field at runtime, so a `BareUid` IS just a string when the
 * code runs. Same technique as `PageRef` in `client/src/providers/page-ref.ts`.
 *
 * WHICH DIRECTION THIS GUARDS. A `BareUid` can be passed anywhere a plain
 * `string` is wanted, but a plain `string` can NOT be passed where a `BareUid`
 * is wanted. That asymmetry is what keeps this type cheap: we brand the places
 * that HAND OUT a uid, and every existing function that merely takes one keeps
 * its plain `string` parameter and needs no change. Branding the taking side as
 * well was measured and rejected for ticket #5188.
 *
 * So a value only becomes a `BareUid` through `toBareUid`, which checks the
 * shape at runtime, or through `asBareUid`, which does not — see its own note.
 */
export type BareUid = string & { readonly __brand: 'BareUid' };

/**
 * Compile-time proof that the brand above actually does its job.
 *
 * This is a type, not a value: it costs nothing at runtime and produces no
 * output. It fails to compile if `BareUid` is ever widened back to plain
 * `string`, which is the one edit that would silently remove every guarantee
 * this file makes while leaving all the code around it looking correct.
 */
type AssertTrue<T extends true> = T;
export type PlainStringIsNotABareUid = AssertTrue<string extends BareUid ? false : true>;

/**
 * Assert that a string already IS a bare Firebase uid, without checking it.
 *
 * WHAT THIS DOES NOT DO: it does not verify anything. There is no test that can
 * tell a bare uid from any other short string, because a uid has no fixed shape
 * beyond "not our document-id prefix". So this is a promise the caller makes,
 * not a fact the code establishes — which is why it exists as one named helper
 * rather than a bare `as BareUid` scattered around: every promise is greppable
 * and every call site is expected to carry a comment saying why the value is
 * trusted there.
 *
 * Use it ONLY where a uid genuinely first enters our code, which is one of:
 *   - a verified Firebase auth token (`decoded.uid` after `verifyIdToken`),
 *   - a field of a payment provider's webhook body that we ourselves stamped
 *     earlier (Paddle's `app_user_id`, PayPro's `x-uid` custom field),
 *   - a Firebase `UserRecord.uid`, from a lookup or an account we just created.
 *
 * Anywhere else, the uid came from somewhere inside our own code and should
 * already be typed `BareUid` — reach for the producer, not for this.
 */
export function asBareUid(trustedBareUid: string): BareUid {
  return trustedBareUid as BareUid;
}

/**
 * Turn a bare Firebase uid into the `_users` document id CouchDB stores it
 * under: `abc123` becomes `org.couchdb.user:abc123`.
 *
 * Throws when it is handed something that already carries the prefix. That is
 * always a programming error — no caller legitimately holds a document id at
 * this point — and the alternative is far worse than an exception: a
 * double-prefixed id (`org.couchdb.user:org.couchdb.user:abc123`) is a perfectly
 * valid string that simply never matches any document, so the read comes back
 * empty and the caller carries on as if the user did not exist. That silent
 * never-match discarded three days of Paddle refund and cancellation events for
 * 7 real accounts, which is the incident this helper exists to prevent.
 */
export function toCouchUserDocId(bareUid: string): string {
  if (bareUid.startsWith(kCouchUserDocIdPrefix)) {
    throw new Error(
      `toCouchUserDocId was given a _users document id, not a bare uid: "${bareUid}". ` +
      'Prefixing it again would produce an id that matches no document.',
    );
  }
  return kCouchUserDocIdPrefix + bareUid;
}

/**
 * Turn a `_users` document id back into the bare Firebase uid the rest of the
 * system passes around: `org.couchdb.user:abc123` becomes `abc123`.
 *
 * Pass the document id itself — for a user document that is `user._id`.
 *
 * Throws when the prefix is not there, for the same reason `toCouchUserDocId`
 * throws on a doubled one: silently returning the input unchanged would let a
 * value of the wrong shape travel on and fail somewhere far away, or not
 * visibly fail at all. The `_users` database also holds design documents, whose
 * ids do not carry this prefix, so a caller walking every row must filter them
 * out before calling this.
 */
export function toBareUid(couchUserDocId: string): BareUid {
  if (!couchUserDocId.startsWith(kCouchUserDocIdPrefix)) {
    throw new Error(
      `toBareUid was given something that is not a _users document id: "${couchUserDocId}". ` +
      `A _users document id always starts with "${kCouchUserDocIdPrefix}".`,
    );
  }
  return couchUserDocId.slice(kCouchUserDocIdPrefix.length) as BareUid;
}

export interface CouchUserInfoBase extends PouchDoc {
  name: string; // internal username generated by firebase (a random string)
  type: 'user'; // it's always "user"
  email: string;
  roles: string[];
  password_scheme: string; // usually "pbkdf2"
  iterations: number;
  derived_key: string;
  salt: string;

  /** See `getUsersDocFormatVersion` / `CURRENT_USERS_DOC_FORMAT_VERSION` above. */
  usersDocFormatVersion?: number;

  consents?: UserConsentRecords;

  licenseType?: UserLicenseType;

  /** Reconciler-derived expiry. `'never'` (= `NEVER_EXPIRES`) means
   * indefinite. Written only by `reconcileLicenseState`; provider code
   * reads per-block `expiresDate_utc` instead.
   *
   * Provider asymmetry on the block side: PPG and GooglePlay callers
   * add `kPPGLicenseGracePeriodHours` to their block `expiresDate_utc`
   * at write time (the reconciler itself does not add grace). Apple
   * stores its block expiry RAW — Apple's own status field
   * (`grace_period` / `billing_retry`) and server notifications cover
   * the renewal-in-flight case, so an extra 24h would over-entitle. */
  licenseExpires_utc?: string;
  licenseByTestKey?: boolean;

  /**
   * The first time this person ever PAID us, on any rail, as an ISO-8601
   * timestamp. Added for #4625, whose gate needs "has been paying for at least
   * 8 weeks" — a question nothing on this record could answer before, because
   * every provider block holds an EXPIRY and no start.
   *
   * **Top-level, not inside a provider block, on purpose** — people move between
   * rails, and a block would reset the date on every migration.
   *
   * **A free trial is not a payment.** Only a real paid charge sets this.
   * PayPro's `licenseActivated_utc` is NOT the same thing — it is set when a
   * trial starts too, so it answers "first activation".
   *
   * **The rule is EARLIEST EVIDENCE WINS, not plain write-once** — see
   * {@link decideFirstPaidAt}, which is its single home.
   *
   * design-docs/review-prompts.md, "Knowing when somebody first paid", carries
   * the rest, and it is worth reading before changing anything here: why
   * write-once would be a trap, which event on each of the four payment rails
   * records this, and why the `_users` conflict merge needs its own line for it.
   */
  firstPaidAt_utc?: string;
}

/**
 * Which caller is asking to record a first-paid date (#4625). The two obey
 * different rules, and mixing them up is the whole risk in this field.
 */
export type FirstPaidAtWriter =
  /** A real paid charge happening now. It may FILL an empty value and nothing else. */
  | 'live'
  /**
   * The one-off backfill replaying the subscription audit logs. It may fill an
   * empty value AND move an existing one earlier, because a log entry is proof
   * of a payment before the one already recorded. It may never move one later.
   */
  | 'backfill';

/**
 * Decide what — if anything — to write to {@link CouchUserInfoBase.firstPaidAt_utc}.
 *
 * Returns the value to store, or `undefined` when the record should be left
 * alone. Pure, so both the server's four payment rails and the one-off backfill
 * tool apply exactly the same rule rather than each re-deriving it.
 *
 * The rule is EARLIEST EVIDENCE WINS. See the field's own comment for why a
 * plain write-once rule is a trap, and design-docs/review-prompts.md for the
 * whole design.
 *
 * A candidate that cannot be parsed is ignored rather than stored, in both
 * modes: a bad timestamp written here would be indistinguishable from a real
 * one later, and the gate that reads it would silently compare against garbage.
 */
export function decideFirstPaidAt(
  existing: string | undefined,
  candidate: string | undefined,
  writer: FirstPaidAtWriter,
): string | undefined {
  // if there is no usable candidate, there is nothing to record
  if (!candidate) return undefined;
  const candidateMillis = DateTime.fromISO(candidate, { zone: 'utc' }).toMillis();
  if (!Number.isFinite(candidateMillis)) return undefined;

  // if nothing is recorded yet, both writers may fill it in
  if (!existing) return candidate;

  // the live path never overwrites: a later charge says nothing about the first one
  if (writer === 'live') return undefined;

  // the backfill may only correct a recorded date EARLIER, never later. An
  // existing value we cannot parse is replaced, because it is not a date we can
  // defend keeping.
  const existingMillis = DateTime.fromISO(existing, { zone: 'utc' }).toMillis();
  if (!Number.isFinite(existingMillis)) return candidate;
  return candidateMillis < existingMillis ? candidate : undefined;
}

export interface CouchUserInfo_Old extends CouchUserInfoBase {
  licenseActivated_utc?: string;
  licenseExpires_utc?: string;
  licenseAutoRenews?: boolean;
  trialEnds_utc?: string;
  trialUsed?: true;  // set to true when the user has used the trial - so we can prevent resubscribing to trial multiple times

  payProSubscriptionId?: string;
  payProInvoiceUrl?: string;
  payProProductId?: string;
  /* this is used to keep track of which users were upgraded when changing to new products
  If this is lower than StoreGetProductsResults.productGeneration, the user should be upgraded to the new product
  to get correct pricing.  
   */
  payProProductGeneration?: number;
}

export interface CouchUserInfo extends CouchUserInfoBase {
  payProData?: CouchUserPayproData;
  /**
   * Paddle web subscription block. Parallel to `payProData` — Paddle is a
   * second web payment provider that runs alongside PayPro. An active
   * block grants the user Premium via `reconcileLicenseState`. Written
   * only by the Paddle webhook handler. See design-docs/paddle-integration.md.
   */
  paddleData?: CouchUserPaddleData;
  subscriptionStatus?: SubscriptionStatus;
  googlePlayData?: CouchUserGooglePlayData;
  appleData?: CouchUserAppleData;
  coachData?: CouchUserCoachData;
  /**
   * Cached snapshot of the derived coach billing state — see
   * `shared/src/data-model/coach-billing.ts` for the shape and
   * `design-docs/coach-slot-pricing.md` for the rationale. Source of
   * truth is the `log_coach_billing` event log; this block is a
   * read-cache for fast `assertCoachNotLocked` checks and gets
   * recomputed by every billing-event-writing handler (PRD §R3.2's
   * cache-update protocol). Absent for pre-migration coaches — its
   * absence is exactly what triggers the legacy passthrough in §R4.5.
   */
  coachBillingData?: CouchUserCoachBilling;
  /**
   * Fixed-tier coach subscription bought in the App Store or Google
   * Play (Phase 2 of coach slot pricing). Parallel to `appleData` /
   * `googlePlayData`, NOT inside them: a coach can hold a personal
   * premium subscription AND a coach-tier subscription in the same
   * store at the same time, so the tier sub needs its own block. An
   * active block grants the coach personal Premium via
   * `reconcileLicenseState`, and its `tierCap` is the concurrent-slot
   * cap enforced at `coachCreateClaimToken`. See
   * `design-docs/coach-slot-pricing.md`.
   */
  coachTierData?: CouchUserCoachTierData;
  /**
   * Paddle per-seat coaching subscription (Phase 4 of coach slot
   * pricing). The web sibling of `coachTierData`: parallel to
   * `paddleData` (the coach's personal Paddle block), NOT inside it,
   * because a become-a-coach handoff moves the coach's Premium from
   * `paddleData` onto this block. An active block grants the coach
   * personal Premium via `reconcileLicenseState` (seat #1 of the
   * coaching subscription), and its `quantity` is `active clients + 1`.
   * See design-docs/paddle-integration.md.
   */
  coachSeatData?: CouchUserCoachSeatData;
  keyData?: CouchUserKeyData;
  magicLinkLastSent_utc?: string;  // to prevent sending too many magic links in a row
  telemetryData?: { [key: string]: string };
  /**
   * Legacy admin-granted client-slot count. This is what still makes a
   * grandfathered coach a coach: nobody was ever moved onto the metered
   * PayPro rail (it never launched, and its code was removed in #4359),
   * so for these coaches the count here is the whole story. A coach on
   * either live rail is gated by their subscription instead — the
   * app-store tier cap, or the Paddle seat quantity.
   * Removable once no grandfathered coaches remain.
   */
  coachMaxSlots?: number;
  scanLockUntil_utc?: string;  // label scan abuse lockout — set when 200 scans/day triggered
  /**
   * A store purchase this user's device reported, which the store could not
   * confirm for us at the time (#4650). It GRANTS NOTHING — it only remembers
   * which transaction to ask about again, so a purchase whose confirmation
   * failed is not lost for good.
   *
   * Its own field rather than a half-filled `appleData` / `googlePlayData`
   * block, deliberately: the reconciler never reads it, so it cannot grant, and
   * `couchFindUserByStoreToken` never matches it, so it cannot be mistaken for
   * a confirmed link. Both of those are load-bearing and neither survives
   * moving the field — read the reasoning before you do, in
   * `design-docs/iap-subscriptions.md`, "A purchase the store could not
   * confirm". The unfindable-by-`couchFindUserByStoreToken` half still holds,
   * but its REASON changed in #4653: the original one (the Apple webhook did
   * not check Apple's signature) went away with #4652, and what keeps it now is
   * that widening that shared lookup would make a parked claim answer
   * `already_linked` to the real buyer. Same doc section.
   *
   * Written by the purchase-confirmation endpoints and cleared by
   * `server/functions/src/helpers/pending-store-claim.ts`.
   */
  pendingStoreClaim?: PendingStoreClaim;
}

/**
 * Which fields of a `_users` document an operator must never see in full, field
 * by field, for the whole document and every block hanging off it.
 * {@link redactCouchUserSecretsForDisplay} is built from this.
 *
 * ADDING A FIELD ABOVE — or a whole new block — MAKES THIS FAIL TO COMPILE until
 * an answer is written here or, for a block, beside that block's own interface.
 * That is deliberate and it is the only reason this exists: the list used to be
 * written by hand a thousand lines below, and nothing failed when somebody added
 * a credential and forgot it.
 *
 * A FUNCTION rather than a plain constant, only because the per-block answers it
 * gathers are declared further down this file. A constant would read them while
 * they are still undefined; a function reads them when it is called. It builds a
 * small object per call, on an admin request that already went to the database.
 */
export function buildCouchUserSecrecyRules(): SecrecyRules<CouchUserInfo> {
  return {
    // CouchDB / PouchDB bookkeeping — identifiers, not secrets.
    _id: false,
    _rev: false,
    _deleted: false,
    db__trans: false,

    // profile and account fields support reads every day
    name: false,
    type: false,
    email: false,
    roles: false,
    usersDocFormatVersion: false,
    consents: false,
    licenseType: false,
    licenseExpires_utc: false,
    licenseByTestKey: false,
    firstPaidAt_utc: false,
    subscriptionStatus: false,
    magicLinkLastSent_utc: false,
    coachMaxSlots: false,
    scanLockUntil_utc: false,
    // arbitrary Mixpanel acquisition properties; nothing we put in it is a
    // credential, and the type names no fields for the compiler to ask about.
    telemetryData: false,

    // the CouchDB password verifier. `derived_key` and `salt` together are what
    // an offline guessing attack needs, so neither is ever shown in full. It has
    // leaked into logs twice — see design-docs/secrets-inventory.md.
    derived_key: true,
    salt: true,
    // the name of the hashing scheme ('pbkdf2') and its work factor. Neither
    // reveals anything about the password on its own. (The LOG redactor in
    // `error-describe.ts` does hide `password_scheme`, on purpose: it matches by
    // field name and errs towards over-matching, which costs nothing there.)
    password_scheme: false,
    iterations: false,

    // provider blocks — each block's answers live beside its own interface
    payProData: kPayproDataSecrecy,
    paddleData: kPaddleDataSecrecy,
    googlePlayData: kGooglePlayDataSecrecy,
    appleData: kAppleDataSecrecy,
    coachData: kCoachDataSecrecy,
    coachBillingData: kCoachBillingSecrecy,
    coachTierData: kCoachTierDataSecrecy,
    coachSeatData: kCoachSeatDataSecrecy,
    keyData: kKeyDataSecrecy,
    pendingStoreClaim: kPendingStoreClaimSecrecy,
  };
}

/** Which store a {@link PendingStoreClaim} is waiting on. */
export type PendingStoreClaimProvider = 'apple' | 'googlePlay';

/**
 * WHICH SUBSCRIPTION a {@link PendingStoreClaim} is for — a person's own
 * membership, or a coach's fixed-tier coaching plan. The two are bought in the
 * same two stores but are written into different blocks by different code, so
 * the re-check has to know which one it is confirming.
 *
 * `'personal'` is the DEFAULT and an absent value means it: a claim parked
 * before this field existed is a personal one, and there is no migration. Keep
 * it that way — a claim survives for up to two weeks, so claims with no rail on
 * them are still being resolved for a fortnight after any deploy that adds one.
 */
export type PendingStoreClaimRail = 'personal' | 'coachTier';

/**
 * A purchase we know the buyer made but the store has not confirmed to us yet.
 * See {@link CouchUserInfo.pendingStoreClaim} for why it lives where it does.
 */
export interface PendingStoreClaim {
  provider: PendingStoreClaimProvider;
  /** Which subscription this is for. Absent means `'personal'` — see
   *  {@link PendingStoreClaimRail}. */
  rail?: PendingStoreClaimRail;
  /** Apple's `originalTransactionId`, or Google Play's `purchaseToken` —
   *  whichever identifier that store's lookup is keyed by.
   *
   *  For Apple this is the id the CLIENT reported, not one Apple has vouched
   *  for, because the whole point is that Apple never answered. The re-check
   *  keys the ownership question on Apple's own authoritative id once it
   *  finally gets one. */
  token: string;
  /** Apple only: the latest transaction id the client reported alongside the
   *  original one. Kept so the re-check has the same pair the purchase had. */
  transactionId?: string;
  /** Google Play only: the product the purchase was made against. Google's
   *  lookup needs it, so without it the claim could never be re-checked. */
  productId?: string;
  /** Google Play only: the caller's obfuscated account id, so the re-check can
   *  run the same this-purchase-belongs-to-this-account check the purchase
   *  path runs. */
  obfuscatedAccountId?: string;
  /** Google Play only: set when the buyer ran this purchase as a replacement of
   *  the subscription on the OTHER rail — a personal purchase replacing their
   *  coach tier, or a coach-tier purchase replacing their personal membership.
   *  Carried so the re-check can still retire that sibling — without it a coach
   *  would end up holding an active personal subscription and an active
   *  coach-tier one on the same store, and pay for both. */
  replacedPurchaseToken?: string;
  /** When the failed confirmation happened. The re-check drops a claim older
   *  than a set number of days, so a purchase that can never be confirmed does
   *  not sit here for ever. */
  parkedAt_utc: string;
  /** When the re-check last asked the store. Absent until it has asked once. */
  lastAttempt_utc?: string;
  /** How many times the re-check has asked and still not got an answer. */
  attempts?: number;
}

/** Which fields of a {@link PendingStoreClaim} an operator must not see in full.
 *  Every field needs an answer — see {@link buildCouchUserSecrecyRules}. */
export const kPendingStoreClaimSecrecy: SecrecyRules<PendingStoreClaim> = {
  provider: false,
  rail: false,
  transactionId: false,
  productId: false,
  obfuscatedAccountId: false,
  parkedAt_utc: false,
  lastAttempt_utc: false,
  attempts: false,
  // `token` holds a Google Play purchase token on a Google Play claim, which IS
  // that store's subscription credential, and an Apple transaction id on an
  // Apple claim, which is not one — the Apple block does not hide its copy
  // either. So the answer depends on which store the claim is waiting on.
  token: (claim) => claim.provider === 'googlePlay',
  // Google Play only, and the same credential as `token`.
  replacedPurchaseToken: (claim) => claim.provider === 'googlePlay',
};

export enum SubscriptionStatus {
  // these match the PayPro API Subscription status
  None = 'none',  // there is no known subscription for this user
  Active = 'active',  // the user is currently subscribed
  Suspended = 'suspended', // the subscription is not recurring, but can be resumed
  Terminated = 'terminated', // the subscription is terminated and can't be resumed. a new one has to be created to resume

  // these are additional statuses we use internally
  Waiting = 'waiting', // the user has started the subscription process, but hasn't completed the payment
  Temporary = 'temporary', // the user completed the payment, but the payment is suspicious, so we are waiting for confirmation

  /** The subscription is PAUSED and is not billing (#4189).
   *
   *  The app never OFFERS a pause — this status is only ever reached from
   *  Paddle's side, via Paddle Retain's cancellation-flow save offer in the
   *  hosted customer portal, or a merchant action in the Paddle dashboard.
   *  Do not go looking for an in-app pause flow; there is none.
   *
   *  Deliberately NOT `Suspended`: `isInGrace` grants access for
   *  Active/Suspended/Temporary, and `computeCanResumeSubscription` treats any
   *  Suspended sub as resumable provider-agnostically — so reusing Suspended
   *  would keep entitlement ON after the pause took effect, the exact bug the
   *  acceptance conditions forbid. `isInGrace` is an ALLOW-LIST, so `Paused` is
   *  non-granting by omission.
   *
   *  A pause takes effect at the end of the paid period: until then the sub
   *  stays `Active` (access retained) carrying a pending
   *  `paddleData.scheduledChange`, and only the `subscription.paused` webhook
   *  flips it here. See design-docs/paddle-integration.md
   *  ("Pause: handled, never offered"). */
  Paused = 'paused',
};
export interface CouchUserPayproData {
  subscriptionStatus: SubscriptionStatus;
  /** Per-provider expiry. ISO datetime, or `'never'` for indefinite
   * (admin-granted) licenses. Reconciler reads this; do not read the
   * top-level `licenseExpires_utc` from inside provider code. */
  expiresDate_utc?: string;
  /** Role this block grants on has-access. Defaults to `Paid`. */
  grantedRole?: AuthRole;
  orderId?: string; // the order id for the last purchase (it stays the same on recurring subs - could be undefined for old users)
  subscriptionId?: string; // NOTE: this may be empty for Waiting and Temporary!
  checkoutId?: string; // the checkout id for the last purchase (this is something we generate, not PPG)
  invoiceUrl?: string;
  productId?: string;
  stateTimeout_utc?: string; // the timeout for the Waiting (or other in the future?) state of the subscription

  /* this is used to keep track of which users were upgraded when changing to new products
  If this is lower than StoreGetProductsResults.productGeneration, the user should be upgraded to the new product
  to get correct pricing.  
   */
  productGeneration?: number;

  licenseActivated_utc?: string;

  trialEnds_utc?: string;
  trialUsed?: true;  // set to true when the user has used the trial - so we can prevent resubscribing to trial multiple times
}

/** Which fields of a {@link CouchUserPayproData} block an operator must not see
 *  in full. Every field needs an answer — see {@link buildCouchUserSecrecyRules}. */
export const kPayproDataSecrecy: SecrecyRules<CouchUserPayproData> = {
  subscriptionStatus: false,
  expiresDate_utc: false,
  grantedRole: false,
  orderId: false,
  subscriptionId: false,
  checkoutId: false,
  productId: false,
  stateTimeout_utc: false,
  productGeneration: false,
  licenseActivated_utc: false,
  trialEnds_utc: false,
  trialUsed: false,
  // the PayPro invoice address carries a token in its query string, so anybody
  // holding the whole address can open that customer's invoice.
  invoiceUrl: true,
};

/**
 * Paddle web subscription block. Mirrors `CouchUserPayproData` (Paddle is
 * a second web payment provider). The reconciler-required fields are
 * `subscriptionStatus`, `expiresDate_utc`, and `grantedRole`; everything
 * else is Paddle-specific.
 *
 * Field-naming convention: this stored block is **camelCase**, while the
 * Paddle webhook `custom_data` keys are **snake_case on the wire** (e.g.
 * stored `checkoutId` ↔ wire `checkout_id`). The webhook handler maps
 * between the two. See design-docs/paddle-integration.md.
 */
export interface CouchUserPaddleData {
  subscriptionStatus: SubscriptionStatus;
  /** Per-provider expiry. ISO datetime, or `'never'` for indefinite.
   *  Reconciler reads this; do not read top-level `licenseExpires_utc`
   *  from inside provider code. Paddle callers add
   *  `kPPGLicenseGracePeriodHours` at write time (provider asymmetry,
   *  same as PPG / Google Play). */
  expiresDate_utc?: string;
  /** Role this block grants on has-access. Defaults to `Paid`. */
  grantedRole?: AuthRole;
  /** Paddle subscription id (`sub_…`). May be empty for Waiting/Temporary. */
  subscriptionId?: string;
  /** Paddle customer id (`ctm_…`). */
  customerId?: string;
  /** Paddle billing-address id (`add_…`) captured from the webhook. Passed as
   *  `customer.address.id` at a later checkout for this returning customer so
   *  Paddle skips its "enter your details" page (#4097). */
  addressId?: string;
  /** Paddle price id (`pri_…`) of the purchased plan (monthly / yearly). */
  priceId?: string;
  /** Last Paddle transaction id (`txn_…`) — the most recent payment. The
   *  charge-replay guard reads {@link chargeTransactionIds} as well, because
   *  this one slot is overwritten by every newer event. */
  transactionId?: string;
  /** The most recent `transaction.completed` ids (`txn_…`) applied to this
   *  subscription, oldest first, capped at a handful. Paddle redelivers and
   *  reorders webhooks, so an OLDER charge can arrive again after a newer one
   *  has replaced {@link transactionId}; any id in this list is a replay and
   *  emits no second revenue event (df-6900af6a7845). Cleared when a confirmed
   *  payment adopts a different `subscriptionId`. */
  chargeTransactionIds?: string[];
  /** Timestamp of the last webhook that validated this block (epoch source
   *  for the reconciler's `lastActive` ordering). */
  lastValidated_utc?: string;
  /** Lifetime flag set the first time the user starts a trial; mirrors
   *  `CouchUserPayproData.trialUsed`. */
  trialUsed?: true;
  /** True while inside the introductory trial of the current subscription;
   *  cleared on the first paid renewal. Mirrors the GP/Apple field. */
  inTrialPeriod?: boolean;
  /** Set once a paid charge (`subscription_started` or `subscription_converted`)
   *  has been counted for this subscription, so later `transaction.completed`
   *  events read as `subscription_recurred` — Paddle's analogue of the stores'
   *  `!wasActiveBefore` start gate. Reset when a confirmed payment adopts a
   *  genuinely different `subscriptionId` (a resubscribe after churn), so the new
   *  sub's first charge counts as a fresh start. (#4082) */
  paidStartCounted?: true;
  /** Set once the TRIAL start has been counted for this subscription, so a paired
   *  `subscription.trialing` / `subscription.created`(trialing) — and any webhook
   *  redelivery, even after a terminal cancel/refund — does not re-emit the trial
   *  `subscription_started`. The trial analogue of {@link paidStartCounted}: a
   *  persisted marker (NOT the mutable `inTrialPeriod`, which the terminal events
   *  clear), reset on the same resubscribe-adopt so a genuinely new subscription's
   *  trial counts fresh. (#4082) */
  trialStartCounted?: true;
  /** A `subscriber_count` -1 that an earlier event owes and the
   *  `subscription.canceled` (or refund) webhook must still send, then clear.
   *  `Terminated` doubles as the webhook's "already decremented" marker, so any
   *  event that writes `Terminated` before that webhook arrives must hand the -1
   *  over through this flag, or it is lost.
   *
   *  Set today by the `subscription.updated` arm when its status is `canceled`
   *  and it ended a counted subscription (#6502). Paddle usually delivers that
   *  update BEFORE the `subscription.canceled`, and without this flag every such
   *  cancel lost its -1.
   *
   *  Original purpose: it was set by `paddleManageSubscription`'s Cancel action
   *  when it locally terminated an already-PAUSED subscription (back when a pause
   *  emitted no -1), and cleared by the `subscription.canceled` / refund webhook
   *  once it had emitted the `subscriber_count` -1. `Terminated` doubles as the
   *  webhook's "already decremented" marker (the -1 fires only when the PRE-event
   *  status was a counted one), so writing `Terminated` immediately at in-app
   *  cancel would have made the real webhook skip the -1 — this flag carried the
   *  still-owed decrement across that hand-off. (#4189) */
  cancelCountOwed?: true;
  /** Set when this person became a Paddle coach while this personal
   *  subscription was still counted in Subscriber Growth (#6524). They stay
   *  counted, now as a coach: the coaching start sent no +1, so this plan's
   *  cancel (which becoming a coach schedules) sends no -1, and is no
   *  cancellation: no event and no cancel date. Cleared when a different
   *  subscription is adopted. */
  movedToCoachSeat?: true;
  /** True when this grant came from a Paddle **sandbox** purchase (an
   *  `e2e-test`-marked checkout). Revenue is suppressed in telemetry.
   *  Mirrors `CouchUserGooglePlayData.isTestPurchase`. */
  isTestPurchase?: boolean;
  /** The checkout id we generate and stamp into `custom_data` at checkout
   *  (stored camelCase ↔ wire `checkout_id`). */
  checkoutId?: string;
  /** Paddle-hosted invoice URL for the last transaction, when available. */
  invoiceUrl?: string;
  /** A change Paddle has SCHEDULED but not yet applied to this subscription.
   *  Paddle's self-service cancel and pause both default to taking effect at
   *  the end of the current billing period: the subscription stays `active`
   *  and Paddle returns a `scheduled_change` describing what will happen and
   *  when. We mirror that here so the User Profile page can show the pending
   *  state ("cancels on …" / "pauses on …") and offer a Resume (un-schedule)
   *  button, and so `getSubscriptionInfo` can gate Cancel/Resume
   *  correctly. Cleared when the change is un-scheduled or once it takes
   *  effect. `action` matches Paddle's `scheduled_change.action` values.
   *  See design-docs/paddle-integration.md ("Self-service management"). */
  scheduledChange?: CouchUserPaddleScheduledChange;
  /** The id (`dsc_…`) of the discount Paddle currently has attached to this
   *  subscription, or absent when there is none (#5084).
   *
   *  **Paddle is the authority for this field.** Three things write it: the
   *  subscription webhook and the admin re-sync, both copying whatever Paddle
   *  reports, and the legacy-price grant action, which writes the id it just
   *  attached so both front-ends show the grant immediately rather than after
   *  the next webhook. That third write is an optimistic head start, not a
   *  second source of truth — the `subscription.updated` webhook fires within
   *  seconds of the attach and re-asserts the same value.
   *
   *  Reading it back from Paddle rather than only stamping it is what makes a
   *  discount attached BY HAND in Paddle's dashboard visible too (the #5072
   *  customer has one, granted on 2026-08-28 before any tooling existed), and
   *  what clears the field when somebody removes one there.
   *
   *  Only the id is stored. The amount, currency and restricted price live on
   *  the discount object at Paddle; a copy here would be a second, staler
   *  version of the figures that decide what a customer is charged.
   *
   *  Read by the legacy-price grant (to refuse granting twice) and by the
   *  monthly ↔ yearly plan switch, which refuses while a discount is attached
   *  because the discount is restricted to one price and the customer would
   *  silently lose it. See design-docs/paddle-integration.md. */
  discountId?: string;
  /** Recency high-water mark for the out-of-order guard: the `occurred_at`
   *  of the newest Paddle event already applied to this block. The webhook
   *  drops any event older than this, so a cancellation delayed behind a
   *  renewal cannot roll the block back and end a paying customer's access.
   *
   *  Keyed by `subscriptionId` — the mark only suppresses an event for the
   *  SAME subscription, so a resubscribe after churn (a new `sub_…`) always
   *  starts fresh. Advanced only when an event is actually applied, never
   *  by an admin or support action: those write at our clock rather than
   *  Paddle's, and advancing from one could drop a real Paddle event that
   *  followed. ISO datetime; `undefined` on older blocks and on any block
   *  whose events all arrived without an `occurred_at`. See
   *  `shared/src/data-model/notification-recency.ts`. */
  lastEventOccurredAt_utc?: string;
  /** Paddle `occurred_at` of the newest entitlement-LOWERING event (refund,
   *  chargeback, cancel, pause, past_due) applied to this block. A raising
   *  event older than this still runs, but may not set the block Active or
   *  extend its expiry — so a redelivered charge landing after a refund
   *  cannot give a refunded customer their access back. Same keying and
   *  advancing rules as `lastEventOccurredAt_utc`. ISO datetime; `undefined`
   *  on older blocks, which then behave as they did before this field. */
  lastLoweringEventOccurredAt_utc?: string;
  /** How current the stored `scheduledChange` is: the newest moment it was
   *  known to be right (#6395). Moved forward, never back, by every writer of
   *  `scheduledChange`: a Paddle event that carries `scheduled_change` (to the
   *  event's `occurred_at`), the in-app or support Cancel / Resume and a
   *  support Re-sync (to our clock, read right after Paddle answered), and the
   *  terminal clears. The webhook ignores the `scheduled_change` of an event
   *  older than this when it would remove the stored change or switch its
   *  action: Paddle sometimes delivers an old event twice, and the late copy
   *  describes the subscription from before the newer change. An older event
   *  with the same action still applies while it is at most 15 minutes older,
   *  because it only refines the date (Paddle's own confirming event for an
   *  in-app cancel is created just before our stamp); older than that it is
   *  ignored too. Kept beside `scheduledChange` rather than inside
   *  it, because a resume deletes `scheduledChange` and must still be
   *  protected. Separate from `discountIdAsOf_utc`, so an in-app cancel cannot
   *  block an older event's discount. ISO datetime; `undefined` on older blocks,
   *  which behave as before. See design-docs/paddle-integration.md. */
  scheduledChangeAsOf_utc?: string;
  /** How current the stored `discountId` is, by exactly the rules of
   *  `scheduledChangeAsOf_utc` (#6395): moved forward by a Paddle event that
   *  carries `discount`, the legacy-price grant and a support Re-sync. An event
   *  older than this does not change `discountId`. ISO datetime; `undefined`
   *  on older blocks, which behave as before. */
  discountIdAsOf_utc?: string;
}

/** The stored form of Paddle's pending `scheduled_change` (see
 *  {@link CouchUserPaddleData.scheduledChange}). Named rather than inline so
 *  the webhook's wire→stored mapper returns exactly this type instead of
 *  restating the shape, which used to let the two drift (#4247). */
export interface CouchUserPaddleScheduledChange {
  action: 'cancel' | 'pause' | 'resume';
  /** When the scheduled change takes effect (UTC ISO). Mirrors Paddle's
   *  `scheduled_change.effective_at`. */
  effectiveAt_utc: string;
}

/** Which fields of a {@link CouchUserPaddleScheduledChange} an operator must not
 *  see in full. Every field needs an answer — see
 *  {@link buildCouchUserSecrecyRules}. */
export const kPaddleScheduledChangeSecrecy: SecrecyRules<CouchUserPaddleScheduledChange> = {
  action: false,
  effectiveAt_utc: false,
};

/**
 * Which fields of a {@link CouchUserPaddleData} block an operator must not see
 * in full. Every field needs an answer — see {@link buildCouchUserSecrecyRules}.
 *
 * Below the block it describes, rather than directly under it, because it names
 * the answers for the nested scheduled change just above: a constant may only
 * read another one that is already declared.
 */
export const kPaddleDataSecrecy: SecrecyRules<CouchUserPaddleData> = {
  subscriptionStatus: false,
  expiresDate_utc: false,
  grantedRole: false,
  subscriptionId: false,
  customerId: false,
  addressId: false,
  priceId: false,
  transactionId: false,
  chargeTransactionIds: false,
  lastValidated_utc: false,
  trialUsed: false,
  inTrialPeriod: false,
  paidStartCounted: false,
  trialStartCounted: false,
  cancelCountOwed: false,
  movedToCoachSeat: false,
  isTestPurchase: false,
  checkoutId: false,
  // a discount id is an opaque Paddle identifier like every other id in this
  // block, and support needs to see it to check a grant — not a secret.
  discountId: false,
  lastEventOccurredAt_utc: false,
  lastLoweringEventOccurredAt_utc: false,
  scheduledChangeAsOf_utc: false,
  discountIdAsOf_utc: false,
  // the Paddle invoice address carries a token, same as the PayPro one.
  invoiceUrl: true,
  // a nested block, so it carries its own answers
  scheduledChange: kPaddleScheduledChangeSecrecy,
};

export interface CouchUserGooglePlayData {
  subscriptionStatus?: SubscriptionStatus;
  expiresDate_utc?: string;
  grantedRole?: AuthRole;
  purchaseToken?: string;
  orderId?: string;
  productId?: string;
  basePlanId?: string;
  lastValidated_utc?: string;
  /** True while the user is inside the introductory free-trial offer of their
   *  current subscription. Cleared on the first non-trial renewal, which is
   *  the charge that fires the `subscription_converted` telemetry event. Used
   *  purely to distinguish "trial → paid" from "ordinary renewal" in
   *  analytics; the subscription source-of-truth is `subscriptionStatus` +
   *  `expiresDate_utc`.
   *
   *  NOT simply "the line item carries an `offerDetails.offerId`" — that was
   *  the reading behind two production bugs (#4256, #4557), because Google
   *  keeps naming the offer a subscription STARTED on for the whole life of
   *  that subscription. Every path that DERIVES this flag from a Google
   *  response goes through `resolveGooglePlayTrialFlag` in
   *  `server/functions/src/helpers/google-play-helpers.ts`, which also requires
   *  that no renewal charge has happened yet, and which leaves the flag alone
   *  when the fresh response cannot say. (Several notification arms also set it
   *  to `false` outright — a paid charge, a refund, an expiry. Clearing it is
   *  always safe; only SETTING it needs the shared decision.)
   *
   *  Absent on records written before the flag shipped (~2026-06). Treat
   *  absent as "not known", never as `false`. */
  inTrialPeriod?: boolean;
  /** Whether Google will charge this subscription again. Read from the
   *  purchase-check response's `lineItems[0].autoRenewingPlan.autoRenewEnabled`
   *  and refreshed on every validate; the `CANCELED` notification clears it and
   *  the renewal-class ones set it, so it stays right between validates.
   *
   *  It exists because Google's stored status cannot answer the question on its
   *  own: a user-initiated cancel and a failed-payment retry both leave the
   *  block `Suspended`, and those are opposite answers. Apple has carried the
   *  equivalent (`autoRenewStatus`) since its own rollout; this is the Google
   *  half (#4080).
   *
   *  THREE-STATE, and the third state matters: `undefined` means "not observed
   *  yet", which is every record written before this shipped. Treat absent as
   *  "will renew" — the `!== false` convention `getSubscriptionInfo.ts` already
   *  uses for Apple — never as cancelled. Reading absent as cancelled would
   *  report the entire existing Google subscriber base as having quit on the day
   *  this shipped. */
  autoRenewing?: boolean;
  /** Lifetime flag set to `true` the first time this user starts an
   *  introductory free-trial offer. Persists across the whole
   *  relationship and is used (alongside `inTrialPeriod`) to detect
   *  the trial-to-paid conversion event. Mirrors
   *  `CouchUserPayproData.trialUsed`. */
  trialUsed?: true;
  /** True when the subscription was purchased by a Google Play license
   *  tester (the `SubscriptionPurchaseV2.testPurchase` block is
   *  present). Persisted so non-renewal RTDN notifications
   *  (CANCELED, EXPIRED, etc.) that don't trigger a fresh validation
   *  round-trip can still tag the Mixpanel profile with the correct
   *  `subscription_environment` value. */
  isTestPurchase?: boolean;
  /** The Google order id (`latestOrderId`) of the last renewal-class
   *  charge (RENEWED / RECOVERED) that already produced a
   *  Mixpanel telemetry event. Google Pub/Sub delivers notifications
   *  at-least-once, so the same charge can arrive twice; the RTDN
   *  telemetry helper compares against this field and suppresses the
   *  duplicate event, so one charge yields exactly one revenue-bearing
   *  event. Deliberately separate from `orderId` above: `orderId` is
   *  refreshed by the validate path WITHOUT emitting events, so reusing
   *  it would wrongly suppress the first event of a charge the client
   *  validated before the webhook arrived. */
  lastTelemetryOrderId?: string;
  /** True once this subscription's cancel has sent `subscriber_count -1` to
   *  Mixpanel (#6502). A resent CANCELED then subtracts nothing, a RESTARTED
   *  (cancel taken back) adds the person back with `+1`, and a later REVOKED
   *  sends no second -1. Cleared when a new start counts the person again.
   *  Read it with {@link readCancelCounted}, which treats a missing or damaged
   *  value as false. See design-docs/telemetry.md, "Cancel and undo on the
   *  stores". */
  cancelCounted?: boolean;
  /** Recency high-water mark for the out-of-order guard: the
   *  `eventTimeMillis` of the newest RTDN already applied to this block.
   *  The webhook drops any notification older than this, so a CANCELED or
   *  EXPIRED delayed behind a RENEWED cannot roll the block back and end a
   *  paying customer's access.
   *
   *  Keyed by `purchaseToken`, so a new subscription (which always mints a
   *  new token) starts with a fresh mark. Advanced only when a notification
   *  is actually applied — an unhandled notification type leaves it alone,
   *  because raising it for an event we did nothing about could suppress a
   *  real one that follows.
   *
   *  Deliberately separate from {@link lastTelemetryOrderId}, which solves
   *  the OTHER half of at-least-once delivery: that one stops a duplicate
   *  telemetry EVENT, this one stops a stale BLOCK WRITE. ISO datetime;
   *  `undefined` on older blocks. See
   *  `shared/src/data-model/notification-recency.ts`. */
  lastNotificationEventTime_utc?: string;
}

/** Which fields of a {@link CouchUserGooglePlayData} block an operator must not
 *  see in full. Every field needs an answer — see
 *  {@link buildCouchUserSecrecyRules}. */
export const kGooglePlayDataSecrecy: SecrecyRules<CouchUserGooglePlayData> = {
  subscriptionStatus: false,
  expiresDate_utc: false,
  grantedRole: false,
  orderId: false,
  productId: false,
  basePlanId: false,
  lastValidated_utc: false,
  inTrialPeriod: false,
  autoRenewing: false,
  trialUsed: false,
  isTestPurchase: false,
  lastTelemetryOrderId: false,
  cancelCounted: false,
  lastNotificationEventTime_utc: false,
  // the purchase token IS Google Play's subscription credential — anybody
  // holding it can ask Google about, and act on, that subscription.
  purchaseToken: true,
};

export interface CouchUserAppleData {
  subscriptionStatus?: SubscriptionStatus;
  expiresDate_utc?: string;
  grantedRole?: AuthRole;
  originalTransactionId?: string;
  transactionId?: string;
  productId?: string;
  lastValidated_utc?: string;
  /** Product Apple will renew to at end of the current cycle. Decoded
   *  from the `signedRenewalInfo` JWS in Apple's App Store Server API
   *  response. When this differs from `productId`, a renewal change is
   *  queued (typically a downgrade scheduled by Switch plan); when it
   *  equals `productId`, no renewal change is pending. Cleared by Apple
   *  when the user disables auto-renew. */
  autoRenewProductId?: string;
  /** Whether auto-renewal is on. Decoded from the `signedRenewalInfo`
   *  JWS's `autoRenewStatus` field (Apple ships `0`=off, `1`=on). When
   *  the user cancels via Settings → Apple ID → Subscriptions, Apple
   *  fires a `DID_CHANGE_RENEWAL_STATUS` notification flipping this to
   *  `false`; the subscription remains Active until expiry. Drives the
   *  client's "Expires On" vs "Renewal Date" label and the
   *  REGULAR-CANCELED block via `canCancelSubscription`. `undefined`
   *  means the field predates the rollout (or Apple omitted the JWS);
   *  treated as "don't gate" so legacy docs keep status-quo behavior. */
  autoRenewStatus?: boolean;

  /** True while the user is inside the introductory free-trial offer
   *  of their current Apple subscription. Set on `SUBSCRIBED` when
   *  the transaction info carries `offerType === 1` (intro offer),
   *  cleared on the first `DID_RENEW` (which fires the
   *  `subscription_converted` telemetry event). Used purely to
   *  distinguish "trial → paid" from "ordinary renewal" in
   *  analytics; the subscription source-of-truth is
   *  `subscriptionStatus` + `expiresDate_utc`. */
  inTrialPeriod?: boolean;
  /** Lifetime flag set to `true` the first time this user starts an
   *  introductory free-trial offer on Apple. Persists across the
   *  whole relationship and is used (alongside `inTrialPeriod`) to
   *  detect the trial-to-paid conversion event. Mirrors
   *  `CouchUserPayproData.trialUsed`. */
  trialUsed?: true;

  /** The Apple transaction id of the last `DID_RENEW` charge that
   *  already produced a Mixpanel telemetry event. Apple server
   *  notifications are delivered at-least-once, so the same renewal
   *  can arrive twice; the notification telemetry helper compares
   *  against this field and suppresses the duplicate event, so one
   *  charge yields exactly one revenue-bearing event. Deliberately
   *  separate from `transactionId` above, which the validate path
   *  refreshes WITHOUT emitting events (Google-side twin:
   *  `CouchUserGooglePlayData.lastTelemetryOrderId`). */
  lastTelemetryTransactionId?: string;

  /** True once this subscription's auto-renew-off has sent
   *  `subscriber_count -1` to Mixpanel (#6502). Same meaning as
   *  `CouchUserGooglePlayData.cancelCounted`: a resent cancel subtracts
   *  nothing, AUTO_RENEW_ENABLED adds the person back with `+1`, and a later
   *  REVOKE sends no second -1. Read it with {@link readCancelCounted}. */
  cancelCounted?: boolean;

  /** Sign-in-with-Apple refresh token, captured on Apple sign-in via
   *  the client → `exchangeAppleAuthCode` cloud-function path and used
   *  by `deleteUser.ts` to call Apple's `/auth/revoke` at deletion time
   *  (so the MH Physique entry disappears from iPhone Settings >
   *  Apple ID > "Apps Using Apple ID"). Owner-readable cleartext —
   *  same trust boundary as the rest of `appleData`. `undefined` for
   *  non-SiwA users and for pre-deploy SiwA users who have not yet
   *  re-signed-in (opportunistic backfill on next sign-in). Apple
   *  invalidates the previous refresh token on each new authorization,
   *  so callers overwrite this field with the latest exchange result. */
  siwaRefreshToken?: string;

  /** Recency high-water mark for the Apple out-of-order guard (V3). Holds
   *  the signing time (`signedDate`) of the last Apple notification — or
   *  the time of the last client-driven validate — applied to this
   *  Apple-lineage block. `appleData` and `coachTierData` share one Apple
   *  `originalTransactionId` across a subscription-group cross-grade, so
   *  `appleServerNotification` reads the newest stamp across BOTH blocks
   *  for the OTID and drops any notification older than it — that is what
   *  stops a late pre-switch notification from resurrecting a retired
   *  block. ISO datetime; `undefined` on blocks written before this field
   *  shipped (treated as "no high-water", so nothing is dropped). See
   *  `design-docs/coach-subscription-coordination.md` §3.1. */
  lastNotificationSignedDate_utc?: string;
}

/** Which fields of a {@link CouchUserAppleData} block an operator must not see
 *  in full. Every field needs an answer — see {@link buildCouchUserSecrecyRules}. */
export const kAppleDataSecrecy: SecrecyRules<CouchUserAppleData> = {
  subscriptionStatus: false,
  expiresDate_utc: false,
  grantedRole: false,
  // Apple's transaction ids are handles support looks the purchase up by, not
  // credentials — nothing can be done with one without Apple's own signing key.
  originalTransactionId: false,
  transactionId: false,
  productId: false,
  lastValidated_utc: false,
  autoRenewProductId: false,
  autoRenewStatus: false,
  inTrialPeriod: false,
  trialUsed: false,
  lastTelemetryTransactionId: false,
  cancelCounted: false,
  lastNotificationSignedDate_utc: false,
  // the Sign-in-with-Apple refresh token is a live credential: it can be
  // exchanged with Apple for access to this person's Apple sign-in.
  siwaRefreshToken: true,
};

/** Coach-granted access block. Created when a user claims a Coach
 *  invite; subscriptionStatus flips to Terminated on detach but the
 *  block is kept for history. */
export interface CouchUserCoachData {
  subscriptionStatus?: SubscriptionStatus;
  /**
   * A REAL expiry, mirrored verbatim from whichever block backs the coach —
   * see `resolveCoachLicenseMirror` (#4361). `'never'` still appears, but only
   * for a coach who has no subscription that can lapse (an admin-granted Legacy
   * coach), and for clients whose grant predates that fix and has not yet been
   * repaired at their next login. It is no longer "the only current shape": a
   * grant with a real date is exactly how a lapsed coach's clients lose access.
   */
  expiresDate_utc?: string;
  grantedRole?: AuthRole;
  coachUid?: string;
  grantedAt_utc?: string;
}

/** Which fields of a {@link CouchUserCoachData} block an operator must not see
 *  in full. Every field needs an answer — see {@link buildCouchUserSecrecyRules}.
 *  Nothing on this block is a credential: it records who granted access and
 *  until when. */
export const kCoachDataSecrecy: SecrecyRules<CouchUserCoachData> = {
  subscriptionStatus: false,
  expiresDate_utc: false,
  grantedRole: false,
  coachUid: false,
  grantedAt_utc: false,
};

/** Fixed-tier coach subscription block (Phase 2). Written only by the
 *  coach-tier paths — `coachTierValidate`, `appleServerNotification`,
 *  and `googlePlayRTDN` — never by the personal-premium validators. It
 *  is a license-granting candidate in `reconcileLicenseState`, so an
 *  active block grants the coach `AuthRole.Paid` (personal Premium).
 *  `tierId` / `tierCap` are resolved from the per-env `coachTierCatalog`
 *  at write time; `tierCap` is the concurrent-client-slot limit the
 *  entitlement seam enforces. See `design-docs/coach-slot-pricing.md`. */
export interface CouchUserCoachTierData {
  /** Which store this tier subscription lives in. Fixed for the life of
   *  the subscription — it is the coach's billing "mode". */
  provider: 'apple' | 'googlePlay';
  subscriptionStatus: SubscriptionStatus;
  /** Per-provider expiry. ISO datetime, or `'never'` for indefinite.
   *  Apple stores the RAW expiry; Google Play callers add
   *  `kPPGLicenseGracePeriodHours` at write time (provider asymmetry,
   *  same as the personal store blocks). */
  expiresDate_utc?: string;
  productId?: string;
  /** Google Play base-plan id of the purchased tier. */
  basePlanId?: string;
  /** Stable internal tier id resolved from `coachTierCatalog`. */
  tierId?: string;
  /** Concurrent client-slot cap resolved from `coachTierCatalog`. The
   *  entitlement seam blocks new claims once
   *  `claimedSlots + pendingInvites >= tierCap`. */
  tierCap?: number;
  /** Apple stored-purchase handle (the original transaction id). */
  originalTransactionId?: string;
  /** Google Play stored-purchase handle. Also the `oldPurchaseToken`
   *  passed to `BillingPlugin.updateSubscription` on a cross-grade. */
  purchaseToken?: string;
  /** Product Apple will renew to at end of the current cycle. Set when a
   *  downgrade is queued; equals `productId` when no change is pending. */
  autoRenewProductId?: string;
  /** Role this block grants on has-access. Defaults to `Paid` — this is
   *  what gives a coach personal Premium for being a paying coach. */
  grantedRole?: AuthRole;
  lastValidated_utc?: string;
  /** Set-only-when-true lifetime flag, matching the sibling store
   *  blocks. */
  trialUsed?: true;
  /** Recency high-water mark for the Apple out-of-order guard (V3), only
   *  meaningful when `provider === 'apple'`. Same role as
   *  `CouchUserAppleData.lastNotificationSignedDate_utc`: it holds the
   *  signing time of the last Apple notification / validate applied to
   *  this coach-tier block, and the webhook reads the newest stamp across
   *  both Apple-lineage blocks (personal + coach) for the shared
   *  `originalTransactionId`. Unused on Google Play (separate tokens, no
   *  shared lineage, no resurrection risk). ISO datetime; `undefined` on
   *  older blocks. See `design-docs/coach-subscription-coordination.md`
   *  §3.1. */
  lastNotificationSignedDate_utc?: string;
  /** The Google Play half of the same recency guard, only meaningful when
   *  `provider === 'googlePlay'`. Same role as
   *  `CouchUserGooglePlayData.lastNotificationEventTime_utc`, keyed by this
   *  block's own `purchaseToken`. It is a SEPARATE field from the Apple
   *  stamp above because the two stores measure different things (Apple's
   *  notification signing time versus Google's event time) and a block only
   *  ever belongs to one store. ISO datetime; `undefined` on older blocks.
   *  See `shared/src/data-model/notification-recency.ts`. */
  lastNotificationEventTime_utc?: string;
  /** The `subscriptionStatus` the coach billing notice last ANNOUNCED to this
   *  coach (#4455). Same role and same rules as the field of this name on
   *  `CouchUserCoachSeatData` — see there for why storing it on a coach block
   *  cannot leak to that coach's clients.
   *
   *  Note the notice this gates is deliberately NEUTRAL on this rail: Google
   *  Play maps four different events, one of them a deliberate cancel, onto
   *  the single `Suspended` status, so it can never claim a payment failed.
   *  See `shared/src/data-model/coach-billing-notice.ts`. */
  lastBillingNoticeStatus?: SubscriptionStatus;
  /** Revenue telemetry markers (#6524). Each one has the same role and rules as
   *  the field of the same name on the personal block of this block's store
   *  (`CouchUserAppleData` for `provider === 'apple'`, `CouchUserGooglePlayData`
   *  for `'googlePlay'`): the store telemetry builders read and write whichever
   *  block the notification routed to, so a coach charge is counted, deduplicated
   *  and cancelled by exactly the rules a personal one is. */
  inTrialPeriod?: boolean;
  /** Apple only: the coach's auto-renew answer. Also what `subscription_paying`
   *  reads, so a coach who cancelled is not reported as still paying. */
  autoRenewStatus?: boolean;
  /** Google only: true for a Play Console licence-tester purchase. */
  isTestPurchase?: boolean;
  /** Apple only. See `CouchUserAppleData.lastTelemetryTransactionId`. */
  lastTelemetryTransactionId?: string;
  /** Google only. See `CouchUserGooglePlayData.lastTelemetryOrderId`. */
  lastTelemetryOrderId?: string;
  /** See `CouchUserAppleData.cancelCounted`. */
  cancelCounted?: boolean;
}

/** Which fields of a {@link CouchUserCoachTierData} block an operator must not
 *  see in full. Every field needs an answer — see
 *  {@link buildCouchUserSecrecyRules}. */
export const kCoachTierDataSecrecy: SecrecyRules<CouchUserCoachTierData> = {
  provider: false,
  subscriptionStatus: false,
  expiresDate_utc: false,
  productId: false,
  basePlanId: false,
  tierId: false,
  tierCap: false,
  originalTransactionId: false,
  autoRenewProductId: false,
  grantedRole: false,
  lastValidated_utc: false,
  trialUsed: false,
  lastNotificationSignedDate_utc: false,
  lastNotificationEventTime_utc: false,
  lastBillingNoticeStatus: false,
  inTrialPeriod: false,
  autoRenewStatus: false,
  isTestPurchase: false,
  lastTelemetryTransactionId: false,
  lastTelemetryOrderId: false,
  cancelCounted: false,
  // a coach tier bought through Google Play carries the same purchase-token
  // credential as a personal Google Play block.
  purchaseToken: true,
};

/**
 * Paddle per-seat coaching subscription block (Phase 4). The web sibling
 * of `CouchUserCoachTierData`: it grants the coach personal Premium (seat
 * #1 of the coaching subscription) and is a `reconcileLicenseState`
 * candidate. The reconciler reads `subscriptionStatus` and `expiresDate_utc`
 * and honors `grantedRole` (defaulting to `Paid`); everything else is
 * Paddle-specific.
 *
 * Field-naming convention matches `CouchUserPaddleData`: this stored block
 * is camelCase while the Paddle webhook `custom_data` keys are snake_case
 * on the wire; the webhook handler maps between the two.
 */
export interface CouchUserCoachSeatData {
  subscriptionStatus: SubscriptionStatus;
  /** Per-provider expiry. ISO datetime, or `'never'` for indefinite.
   *  Reconciler reads this; do not read the top-level `licenseExpires_utc`
   *  from inside provider code. Paddle callers add
   *  `kPPGLicenseGracePeriodHours` at write time (provider asymmetry, same
   *  as PPG / Google Play / the personal Paddle block). */
  expiresDate_utc?: string;
  /** Role this block grants on has-access. Defaults to `Paid` — this is
   *  what gives a coach personal Premium via seat #1 of the coaching sub. */
  grantedRole?: AuthRole;
  /** Paddle subscription id (`sub_…`) of the coaching subscription. May be
   *  empty for Waiting/Temporary. */
  subscriptionId?: string;
  /** Paddle customer id (`ctm_…`). */
  customerId?: string;
  /** Paddle billing-address id (`add_…`) captured from the webhook. Stored for
   *  symmetry with the personal block so a later coach checkout can skip the
   *  "enter your details" page (#4097). */
  addressId?: string;
  /** Paddle price id (`pri_…`) of the coach-seat price — the routing
   *  discriminator the webhook keys on, and the price the seat-quantity
   *  sync updates. */
  priceId?: string;
  /** Current seat quantity on the coaching subscription = active client
   *  seats + 1 (the coach's own seat), floored at 1. */
  quantity?: number;
  /** Timestamp of the last webhook that validated this block (epoch source
   *  for the reconciler's `lastActive` ordering). */
  lastValidated_utc?: string;
  /** True when this grant came from a Paddle **sandbox** purchase (an
   *  `e2e-test`-marked checkout). Revenue is suppressed in telemetry.
   *  Mirrors `CouchUserPaddleData.isTestPurchase`. */
  isTestPurchase?: boolean;
  /** A change Paddle has SCHEDULED but not yet applied to this COACHING
   *  subscription (#4416). Same object, same meaning and the same type as
   *  `CouchUserPaddleData.scheduledChange`: a cancel or pause set up in
   *  Paddle's dashboard takes effect at the end of the current billing period,
   *  and until then the subscription stays `active` and nothing else moves.
   *
   *  It is the single most valuable thing a support re-sync of a coach can
   *  discover, precisely because every other field still agrees with what we
   *  already had. Cleared when the change is un-scheduled, when it takes
   *  effect, and on every route that ends the subscription — a pending cancel
   *  left on a dead block would tell Phase 3's Resume rules there is something
   *  to resume. See design-docs/coach-slot-pricing.md
   *  ("`coachSeatData.scheduledChange`"). */
  scheduledChange?: CouchUserPaddleScheduledChange;
  /** Recency high-water mark for the out-of-order guard, keyed by this
   *  block's own `subscriptionId`. Exactly the same role and rules as
   *  `CouchUserPaddleData.lastEventOccurredAt_utc` — the coaching
   *  subscription needs the guard for the same reason the personal one
   *  does. ISO datetime; `undefined` on older blocks. See
   *  `shared/src/data-model/notification-recency.ts`. */
  lastEventOccurredAt_utc?: string;
  /** Same role and rules as `CouchUserPaddleData.lastLoweringEventOccurredAt_utc`. */
  lastLoweringEventOccurredAt_utc?: string;
  /** Same role and rules as `CouchUserPaddleData.scheduledChangeAsOf_utc`. */
  scheduledChangeAsOf_utc?: string;
  /** The `subscriptionStatus` the coach billing notice last ANNOUNCED to this
   *  coach (#4455), so a redelivered or repeated webhook cannot tell them the
   *  same thing twice. Cleared when the block returns to a healthy status, so
   *  a coach who fails, pays, and fails again next month is told both times.
   *  `undefined` means nothing has been announced.
   *
   *  Safe to keep here even though a client's `coachData` is a verbatim copy
   *  of this block: `buildMirroredCoachData` composes a FRESH object from a
   *  fixed field list rather than copying the block, so this never reaches a
   *  client. See `design-docs/coach-subscription-lapse.md`. */
  lastBillingNoticeStatus?: SubscriptionStatus;
  /** The upcoming Paddle charge, captured right after a seat-quantity change
   *  (#4455). Stored rather than fetched on demand so the notification and the
   *  in-app billing page can never quote different figures, and so drawing the
   *  billing page costs no Paddle round-trip.
   *
   *  All three are absent together when that read failed — the notice is then
   *  sent WITHOUT an amount rather than not at all, and never with a guess.
   *  The amount is in the currency's smallest unit, as Paddle sends it. */
  nextBilledAmountMinor?: string;
  nextBilledCurrency?: string;
  nextBilledAt_utc?: string;
  /** The seat change the "seat count changed" notice last told this coach
   *  about (df-4546f588192e). Written only by the seat-quantity sync, inside
   *  its 409-retrying save, so when two syncs race to announce the SAME change
   *  only the one whose save records it sends the notice. `quantity` cannot
   *  serve: Paddle's webhook echo of our own change writes that too.
   *  `undefined` on blocks older than this field, which simply notify as
   *  before. See `design-docs/coach-subscription-lapse.md`. */
  lastSeatNotice?: CouchUserCoachSeatNotice;
  /** Revenue telemetry markers (#6524). Same role and rules as the fields of the
   *  same names on `CouchUserPaddleData`: the coaching subscription is counted in
   *  Subscriber Growth and its charges are deduplicated exactly as a personal
   *  Paddle subscription's are. See there for each one. */
  paidStartCounted?: true;
  cancelCountOwed?: true;
  chargeTransactionIds?: string[];
}

/** One seat-count change the coach was told about. A later sync counts as a
 *  repeat of it only when all three fields match: the change is keyed by the
 *  subscription and by BOTH counts, because the stored `quantity` also moves by
 *  other routes (a new coaching subscription starts at 1; a seat change made in
 *  Paddle's dashboard), and a told count alone would then read a real change
 *  that happens to land on the same number as already announced. */
export interface CouchUserCoachSeatNotice {
  subscriptionId: string;
  /** The count the sync read before its Paddle change; absent when none was
   *  stored (the coach's first sync, which announces nothing). */
  previousQuantity?: number;
  newQuantity: number;
}

/** Which fields of a {@link CouchUserCoachSeatNotice} an operator must not see
 *  in full. Every field needs an answer — see {@link buildCouchUserSecrecyRules}. */
export const kCoachSeatNoticeSecrecy: SecrecyRules<CouchUserCoachSeatNotice> = {
  subscriptionId: false,
  previousQuantity: false,
  newQuantity: false,
};

/** Which fields of a {@link CouchUserCoachSeatData} block an operator must not
 *  see in full. Every field needs an answer — see
 *  {@link buildCouchUserSecrecyRules}.
 *
 *  Nothing here is a credential today. Note what this block does NOT have: the
 *  personal Paddle block's `invoiceUrl`, which is the one Paddle field that
 *  carries a token. If one is ever added here it needs the same answer as
 *  there. */
export const kCoachSeatDataSecrecy: SecrecyRules<CouchUserCoachSeatData> = {
  subscriptionStatus: false,
  expiresDate_utc: false,
  grantedRole: false,
  subscriptionId: false,
  customerId: false,
  addressId: false,
  priceId: false,
  quantity: false,
  lastValidated_utc: false,
  isTestPurchase: false,
  scheduledChange: kPaddleScheduledChangeSecrecy,
  lastEventOccurredAt_utc: false,
  lastLoweringEventOccurredAt_utc: false,
  scheduledChangeAsOf_utc: false,
  lastBillingNoticeStatus: false,
  nextBilledAmountMinor: false,
  nextBilledCurrency: false,
  nextBilledAt_utc: false,
  lastSeatNotice: kCoachSeatNoticeSecrecy,
  paidStartCounted: false,
  cancelCountOwed: false,
  chargeTransactionIds: false,
};

/** Activation-key-granted access block. Created when a user redeems
 *  an activation code. `grantedRole` carries the key's `keyRecord.role`
 *  (Paid or Vip), so VIP keys survive reconciliation without role
 *  downgrade across cross-provider transitions.
 *
 *  This block is ALSO how an access grant made by hand is recorded — an
 *  officer using the admin permissions screen, or a one-off goodwill-grant
 *  script. Those carry a made-up (synthetic) `activationCode` instead of a
 *  real redeemed key, which is what tells the two apart. See
 *  `shared/src/data-model/officer-grant.ts`. */
export interface CouchUserKeyData {
  subscriptionStatus?: SubscriptionStatus;
  /** `'never'` allowed for infinite/VIP keys. */
  expiresDate_utc?: string;
  grantedRole?: AuthRole;
  activationCode?: string;
  redeemedAt_utc?: string;
  isTestKey?: boolean;
  /** Set only on a grant an officer made by hand, and holds the id of the
   *  officer who made it. Absent on a real key redemption and on the older
   *  script-written grants, so its absence never means anything on its own —
   *  ask `isSyntheticActivationCode(activationCode)` instead, which is the
   *  check that covers every synthetic grant we have ever written.
   *
   *  Optional and purely additive: a server that does not know this field
   *  reads the rest of the block exactly as before and reaches the same
   *  decision, so adding it needs no `_users` format-version change. */
  grantedByOfficerUid?: string;
}

/** Which fields of a {@link CouchUserKeyData} block an operator must not see in
 *  full. Every field needs an answer — see {@link buildCouchUserSecrecyRules}. */
export const kKeyDataSecrecy: SecrecyRules<CouchUserKeyData> = {
  subscriptionStatus: false,
  expiresDate_utc: false,
  grantedRole: false,
  redeemedAt_utc: false,
  isTestKey: false,
  grantedByOfficerUid: false,
  // an activation code can be redeemed by whoever reads it, so it is withheld
  // like any other credential.
  activationCode: true,
};

export type CouchUserInfoAny = CouchUserInfo_Old | CouchUserInfo;
export function isCouchUserInfo_Old(info: CouchUserInfoAny): info is CouchUserInfo_Old {
  return (info as CouchUserInfo_Old).payProSubscriptionId !== undefined || (info as CouchUserInfo_Old).payProProductGeneration !== undefined;
}

// Take a CouchUserInfo object that is potentially old format, and normalize it to the latest version
export function normalizeCouchUserInfo(info: CouchUserInfoAny, now: DateTime): CouchUserInfo {
  if (isCouchUserInfo_Old(info)) {
    let { licenseActivated_utc, licenseAutoRenews, trialEnds_utc, trialUsed, payProSubscriptionId, payProInvoiceUrl, payProProductId, payProProductGeneration, ...rest } = info;
    let status: SubscriptionStatus;
    status = SubscriptionStatus.None;

    // if it has subscription, and subscription has expiry time
    if (typeof payProSubscriptionId === 'string' && payProSubscriptionId.length > 0 && info.licenseExpires_utc) {
      let expirationTime = DateTime.fromISO(info.licenseExpires_utc);
      // if the expiration time is in the future
      if (expirationTime >= now) {
        if (licenseAutoRenews) {
          status = SubscriptionStatus.Active;
        } else {
          status = SubscriptionStatus.Suspended;
        }
      } else {
        // NOTE: In the old system, we didn't remember enough data to know whether we can resume a canceled subscription, that has already expired
        // we will assume that the subscription is terminated in this case, so they have to reenter the payment details again if they want to resume
        status = SubscriptionStatus.Terminated;
      }
    }

    return {
      ...rest,
      subscriptionStatus: status,
      payProData: {
        subscriptionStatus: status,
        orderId: undefined,
        subscriptionId: payProSubscriptionId,
        invoiceUrl: payProInvoiceUrl,
        productId: payProProductId,
        productGeneration: payProProductGeneration,
        licenseActivated_utc,
        licenseAutoRenews,
        trialEnds_utc,
        trialUsed,
      }
    } as CouchUserInfo;
  } else {
    if (info.payProData === undefined) {
      info.payProData = {
        subscriptionStatus: SubscriptionStatus.None,
      };
    }
    // populate top-level subscriptionStatus from payProData if missing
    if (info.subscriptionStatus === undefined) {
      info.subscriptionStatus = info.payProData.subscriptionStatus;
    }
    return info;
  }
}

export function isUserSubscriptionActive(info: CouchUserInfo, now: DateTime): boolean {
  if (info && info.subscriptionStatus === SubscriptionStatus.Active) {
    if (info.licenseExpires_utc === NEVER_EXPIRES) {
      // indefinite licenses (VIP keys, indefinite Coach grants, admin-granted indefinite)
      return true;
    }
    if (info.licenseExpires_utc) {
      let expirationTime = DateTime.fromISO(info.licenseExpires_utc);
      return expirationTime >= now;
    }
  }
  return false;
}

/** The per-provider blocks `isSubscriberInFreeTrial` actually reads. Declared as
 *  a structural subset (rather than the full `CouchUserInfo`) so the CLIENT can
 *  pass its `CouchLoginResult` directly — that type carries the Apple / Google /
 *  Paddle blocks but no `payProData`, and a structural parameter lets the
 *  compiler check the call instead of forcing an `as unknown as` cast that would
 *  hide a future divergence.
 *
 *  `payProData` is narrowed further, to just the ONE field the rule reads. The
 *  full `CouchUserPayproData` has a REQUIRED `subscriptionStatus`, and the client
 *  does not have one to give: `CouchLoginResult` exposes the PayPro trial end as
 *  a loose top-level `trialEnds_utc` and no PayPro block at all. Asking the
 *  caller for the whole block would force it to invent a filler status, and a
 *  made-up value inside a predicate's input is how a wrong answer gets born. A
 *  real `CouchUserPayproData` still satisfies this shape, so every server caller
 *  passing a full `CouchUserInfo` is unaffected. (#4325) */
export type SubscriberTrialBlocks = Pick<CouchUserInfo, 'appleData' | 'googlePlayData' | 'paddleData'>
  & { payProData?: { trialEnds_utc?: string } };

/**
 * The PADDLE half of the trial rule, split out so it can be asked WITHOUT a clock
 * (#4248 Phase 7). Paddle's trial signal needs no date comparison — unlike
 * PayPro's, which is "is `now` before `trialEnds_utc`" — so a caller that only
 * cares about Paddle does not have to invent a `now` it has no business having.
 *
 * The capability matrix (`subscription-action-capability.ts`) is that caller: it
 * is a pure, clock-free decision function shared by the server, the Admin UI and
 * the CLI, and it must refuse a plan switch during a trial. Calling this keeps
 * ONE copy of the rule — a second hand-written `paidStartCounted` check is
 * exactly how #4184 happened.
 *
 * Two signals, either sufficient:
 *   - `inTrialPeriod === true`, when Paddle bothered to set it;
 *   - an ACTIVE block that has not counted a paid charge (`paidStartCounted`),
 *     which is the authoritative one. Paddle does NOT set `inTrialPeriod`
 *     reliably — in production roughly 30 of 31 active Paddle "paid" blocks were
 *     really trials with the flag undefined (#4184).
 */
export function isPaddleSubscriberInFreeTrial(
  paddleData: { subscriptionStatus?: SubscriptionStatus; inTrialPeriod?: boolean; paidStartCounted?: boolean } | undefined,
): boolean {
  if (paddleData?.inTrialPeriod === true) return true;
  return paddleData?.subscriptionStatus === SubscriptionStatus.Active && paddleData?.paidStartCounted !== true;
}

/**
 * The Apple / Google Play half of the trial rule: the block says it is in a
 * trial AND that trial's end (the block's expiry) has not passed yet.
 *
 * The expiry check exists because nothing clears `inTrialPeriod` when a store
 * trial ends unpaid. The flag stays true on the dead block for good, so a person
 * who let an Apple trial lapse and later paid on Paddle read as "in a trial"
 * forever, and every paying-subscriber count left them out (#6683). The Manage
 * Subscription page has always used this same expiry test (#3791).
 *
 * A missing or unreadable expiry keeps the old answer, "still a trial": we cannot
 * tell the trial is over, and counting someone as paying who is not is the
 * worse mistake.
 */
function isStoreTrialStillRunning(
  block: { inTrialPeriod?: boolean; expiresDate_utc?: string } | undefined,
  now: DateTime,
): boolean {
  if (block?.inTrialPeriod !== true) return false;
  if (!block.expiresDate_utc) return true;
  const trialEnd = DateTime.fromISO(block.expiresDate_utc, { zone: 'utc' });
  return !trialEnd.isValid || trialEnd > now;
}

/**
 * True when the user's currently-active subscription is a FREE TRIAL — they have
 * access now but are NOT yet a paying subscriber.
 *
 * This is THE canonical trial rule. Both the server daily `paid_subscription_active`
 * recompute (`syncSubscriptionActiveToMixpanel`) and the #4055 historical backfill
 * (`tools/src/backfill-paid-subscriber-profile-4055-*`) import it, so the two can never
 * drift again (they had drifted — the daily recompute used a Paddle rule the backfill
 * did not; see #4184). `paid_subscription_active = hasAccess && !inTrial && !testPurchase`.
 *
 * Provider trial signals differ:
 *   - Apple / Google set `inTrialPeriod === true` reliably at trial start, but never
 *     clear it when a trial ends unpaid, so it counts only until the block's expiry
 *     (see `isStoreTrialStillRunning`, #6683).
 *   - Paddle does NOT reliably set `inTrialPeriod` (observed in prod: active Paddle
 *     trials leave it undefined). Its authoritative "a paid charge was counted" marker
 *     is `paidStartCounted`, so an ACTIVE Paddle block that has not counted a paid start
 *     is still a trial. This is the #4184 bug the old `inTrialPeriod`-only check missed
 *     (~30 of 31 active Paddle "paid" blocks were actually trials).
 *   - PayPro has no `inTrialPeriod`; its trial is running only while `now` is before the
 *     recorded `trialEnds_utc` (which self-clears once the trial date passes).
 */
export function isSubscriberInFreeTrial(info: SubscriberTrialBlocks, now: DateTime): boolean {
  // if any store block is flagged as currently inside a trial that has not ended yet
  if (isStoreTrialStillRunning(info.appleData, now)) return true;
  if (isStoreTrialStillRunning(info.googlePlayData, now)) return true;
  if (isPaddleSubscriberInFreeTrial(info.paddleData)) return true;
  // if PayPro's recorded trial end is still in the future, the trial is running
  // (parse as UTC — the field is `_utc` by contract; real values are Z-suffixed).
  // A future `trialEnds_utc` is sufficient on its own — we deliberately do NOT also
  // require `trialUsed`: a running trial always has both set, and a never-trialed paying
  // sub has no `trialEnds_utc`, so the extra guard would never change the answer. This
  // matches the server daily-recompute's long-standing live rule (which never checked
  // `trialUsed`); the #4055 backfill's earlier tools copy had the redundant guard, and
  // unifying here drops it. Pinned by the "future trialEnds without trialUsed" test.
  const payProTrialEnd = info.payProData?.trialEnds_utc;
  if (payProTrialEnd && DateTime.fromISO(payProTrialEnd, { zone: 'utc' }) > now) return true;
  return false;
}

/**
 * True when the user's subscription is a sandbox / test purchase — never a real paying
 * customer, so it must be excluded from `paid_subscription_active`. Only the providers
 * that persist the flag carry it (Paddle, Google Play). Apple has no persisted per-block
 * test flag (sandbox is only known at telemetry time), so an Apple sandbox sub is not
 * detectable from the stored doc — an accepted, documented gap. Canonical rule shared by
 * the daily recompute and the #4055 backfill (#4184).
 *
 * The parameter is the two blocks this actually reads, not the whole
 * `CouchUserInfo`, for the same reason `SubscriberTrialBlocks` above is a
 * structural subset: the CLIENT calls this too (#4325, the Refiner
 * `paid_subscription_active` trait) and only has a `CouchLoginResult`, which
 * carries both of these blocks but is not a `CouchUserInfo`. Server callers pass
 * a full `CouchUserInfo`, which still satisfies this shape.
 */
export function isSubscriberTestPurchase(info: Pick<CouchUserInfo, 'paddleData' | 'googlePlayData'>): boolean {
  if (info.paddleData?.isTestPurchase === true) return true;
  if (info.googlePlayData?.isTestPurchase === true) return true;
  return false;
}

/**
 * Has this store subscription's cancel already sent `subscriber_count -1` to
 * Mixpanel (#6502)? Only an explicit `true` counts. A missing block, a missing
 * field, or a value of the wrong type (a document an older or broken writer
 * saved) all read as false, which means "not subtracted yet".
 */
export function readCancelCounted(block: Pick<CouchUserAppleData, 'cancelCounted'> | undefined): boolean {
  return block?.cancelCounted === true;
}

/**
 * Whether the user's LOCAL session data says they hold a PAUSED Paddle
 * subscription (#4189).
 *
 * Deliberately independent of the server's `getSubscriptionInfo` response: a
 * paused subscription grants no access, so the user's role drops to Unknown and
 * anything derived from that response is unavailable when the call fails. Every
 * paused affordance — suppressing the Upgrade block, showing Resume, blocking a
 * second purchase, dispatching the Resume action — has to keep working in that
 * state, so they all read this one rule instead of each re-deriving it.
 */
export function isPausedPaddleSubscriber(
  info: { licenseType?: UserLicenseType; paddleData?: { subscriptionStatus?: SubscriptionStatus } } | undefined,
): boolean {
  return info?.licenseType === UserLicenseType.Paddle
    && info.paddleData?.subscriptionStatus === SubscriptionStatus.Paused;
}

/// Update telemetry data in a user info object with new data. This only sets the data if NONE of it is already present
/* Rationale: if the user was acquired initially through one channel, we don't want to overwrite that with a different channel later.
   Exception: if the stored referrer is 'direct', upgrade it with a real referrer when one becomes available.
*/
export function updateUserTelemetryData(info: CouchUserInfo, newData: { [key: string]: string }) {
  if (!info.telemetryData) {
    info.telemetryData = { ...newData };
    return;
  }

  if (info.telemetryData['$referrer'] === 'direct' && newData['$referrer'] && newData['$referrer'] !== 'direct') {
    // upgrade 'direct' referrer with real one
    info.telemetryData['$referrer'] = newData['$referrer'];
    if (newData['$referrer_domain']) {
      info.telemetryData['$referrer_domain'] = newData['$referrer_domain'];
    }
  }

  fillMissingClickIds(info.telemetryData, newData);
}

/**
 * Fill in ad click ids the stored bag does NOT have yet, without ever changing
 * one it does (#5408).
 *
 * Why this exists. Since accounts started recording where their owner came from
 * at signup, the all-or-nothing rule above stopped being enough on its own. An
 * account whose signup URL carried any campaign tag — a newsletter link, say —
 * already has `telemetryData`, so a later purchase used to store nothing at all.
 * On PayPro that loses the sale's click id about two weeks later: its checkout
 * blob arrives in one IPN's custom field and is simply absent from the
 * trial-to-paid IPN that follows, which then reports the conversion with no
 * click id and no way to match it to the ad. (Paddle does not have that problem —
 * it keeps the checkout data on the subscription and echoes it on every later
 * webhook — but the rule is written once for both.)
 *
 * Filling only what is MISSING keeps the first-touch promise: the channel that
 * originally acquired someone is never rewritten by a later visit.
 *
 * **The Facebook click id and its timestamp are ONE unit, written from ONE
 * source or not at all.** Filling them independently is how #4045 comes back
 * from the other direction: a document holding a click id from an older build
 * that recorded no time would otherwise take THIS checkout's timestamp, giving
 * Meta `fb.1.<time of a different click>.<the old click id>` — a value describing
 * a click that never happened. A click id with no time is fine; the pixel
 * builder documents the fallback. A click id with the WRONG time is not.
 */
function fillMissingClickIds(stored: { [key: string]: string }, newData: { [key: string]: string }) {
  // the Facebook pair, as one unit
  if (!stored['fbclid'] && newData['fbclid']) {
    stored['fbclid'] = newData['fbclid'];
    if (newData['fbclid_ts']) {
      stored['fbclid_ts'] = newData['fbclid_ts'];
    } else {
      // this checkout supplied no capture time, so any time already stored
      // belongs to some OTHER click and must not be paired with this id. Better
      // no time at all — the pixel builder falls back to now — than a time from
      // a different click, which is the #4045 error itself.
      delete stored['fbclid_ts'];
    }
  }
  // Google's click id carries no timestamp, so it stands alone
  if (!stored['gclid'] && newData['gclid']) {
    stored['gclid'] = newData['gclid'];
  }
}

// ---------------------------------------------------------------------------
// Secret redaction for admin display
//
// The admin "MH Physique Info" card prints a whole user document. The `_users`
// doc carries the password hash (`derived_key` + `salt`), payment-provider
// credentials (the Google Play `purchaseToken`, the PayPro / Paddle
// `invoiceUrl`), the Apple sign-in refresh token (`siwaRefreshToken`) and a
// redeemable activation code — none of which an operator ever needs to read in
// full. These helpers censor those fields so the document can be shown safely.
//
// NOTE: only `adminGetUserDetails` applies these helpers today. The separate
// `firebase-cmd user:show` command dumps the Firebase Auth UserRecord (a
// DIFFERENT object, not this CouchDB doc) un-redacted — a known, deliberately
// uncovered gap, not something this redactor guards.
// ---------------------------------------------------------------------------

/** The run of stars {@link maskSecretForDisplay} puts where the hidden part was. */
const kMaskStars = '****';

/**
 * Censor a secret string for on-screen display, keeping only a few leading and
 * trailing characters so an operator can still tell two values apart or confirm
 * which record they are looking at, without the full secret ever being shown.
 *
 * The rule keeps at most four characters at each end, and never more than a
 * quarter of the length from each side, so at least half of every value stays
 * hidden. A short value is censored completely — revealing the ends of a short
 * secret (an activation code) would give away most of it.
 *
 * A value that is already masked comes back unchanged (#6418). The audit-log
 * scrub and account deletion both mask rows that were masked when written, and
 * a second pass would otherwise hide a little more of the value each time.
 */
export function maskSecretForDisplay(value: string): string;
export function maskSecretForDisplay(value: string | undefined): string | undefined;
export function maskSecretForDisplay(value: string | undefined): string | undefined {
  // nothing to hide in an absent or empty value — pass it through unchanged so
  // the caller can mask an optional field without a null check of its own.
  if (value === undefined || value.length === 0) {
    return value;
  }
  // if a previous pass already masked it, masking again would only shorten the ends
  if (isMaskedSecretForDisplay(value)) {
    return value;
  }
  const keep = Math.min(4, Math.floor(value.length / 4));
  // below three revealed characters per end there is not enough length to show
  // the ends without exposing most of the value, so hide it entirely.
  if (keep < 3) {
    return kFullyCensoredValue;
  }
  return `${value.slice(0, keep)}${kMaskStars}${value.slice(value.length - keep)}`;
}

/**
 * Whether a value has exactly a shape {@link maskSecretForDisplay} produces:
 * `****` alone, or 3 or 4 kept characters on each side of `****` (10 or 12
 * characters in all). No real secret we store has that shape: hashes and tokens
 * are long and hold no stars, invoice links are long URLs.
 */
export function isMaskedSecretForDisplay(value: string): boolean {
  if (value === kFullyCensoredValue) {
    return true;
  }
  // for each number of kept characters the mask can leave, check the stars sit exactly in the middle
  for (const keep of [3, 4]) {
    if (value.length === keep * 2 + kMaskStars.length && value.slice(keep, keep + kMaskStars.length) === kMaskStars) {
      return true;
    }
  }
  return false;
}

/**
 * Return a deep copy of a user document with every secret field censored, safe
 * to show on the admin screen or dump to a terminal. The passed object is not
 * modified — the server still needs the real values for its own provider
 * comparisons — so this must be applied to a COPY that is sent to the client,
 * not to the working document.
 *
 * SECURITY: apply this on the SERVER, before the document is sent to the
 * browser, so the un-censored secret never leaves the backend at all. Masking
 * only on the client would still ship the real secret in the network response.
 *
 * The censored fields are named one at a time rather than matched by key name,
 * so adding a field to the document never silently censors or exposes it — a new
 * secret field has to be answered for on purpose. Those answers live beside the
 * interfaces that declare the fields, and {@link buildCouchUserSecrecyRules}
 * gathers them; the compiler refuses a field with no answer, which is what keeps
 * this in step with the document instead of a comment asking people to remember.
 */
export function redactCouchUserSecretsForDisplay(user: CouchUserInfo): CouchUserInfo {
  // the user doc is plain JSON, so a round-trip is a safe deep clone and keeps
  // us from mutating the caller's object.
  const clone: CouchUserInfo = JSON.parse(JSON.stringify(user));
  applySecrecyRules(clone, buildCouchUserSecrecyRules(), maskSecretForDisplay);
  return clone;
}

/**
 * Return a deep copy of a user document with every secret field censored, for
 * storing in the subscription audit log (`log_subscription_transactions`).
 *
 * Same rules and same censoring as {@link redactCouchUserSecretsForDisplay}, so
 * two rows for the same token can still be told apart by its ends. Two things
 * differ: it accepts the legacy document shape, because a snapshot is taken of
 * whatever the database held; and it also censors that shape's top-level
 * `payProInvoiceUrl`, which the rules do not name because the current shape
 * moved it into `payProData`.
 *
 * Why the audit log needs this at all: a row there used to carry the whole
 * document, password hash and provider tokens included, and one was printed
 * while debugging (`design-docs/secrets-inventory.md`, 2026-10-10).
 */
export function redactCouchUserSecretsForAuditLog(user: CouchUserInfoAny): CouchUserInfoAny {
  // a JSON round-trip is a safe deep clone of a plain user doc and leaves the
  // caller's working object untouched.
  const clone = JSON.parse(JSON.stringify(user)) as CouchUserInfo & Pick<CouchUserInfo_Old, 'payProInvoiceUrl'>;
  applySecrecyRules(clone, buildCouchUserSecrecyRules(), maskSecretForDisplay);
  // if the doc still has the legacy invoice link, censor it the same way
  const legacyInvoiceUrl: unknown = clone.payProInvoiceUrl;
  if (legacyInvoiceUrl !== undefined && legacyInvoiceUrl !== null) {
    clone.payProInvoiceUrl = typeof legacyInvoiceUrl === 'string'
      ? maskSecretForDisplay(legacyInvoiceUrl)
      : kFullyCensoredValue;
  }
  return clone;
}
